<div class="contact-detail-shell" *ngIf="!loading(); else loadingState">
  <header class="contact-detail-header">
    <div>
      <h1>{{ record()?.company_name || 'Company Contact' }}</h1>
    </div>
    <div class="contact-detail-header__actions">
      <button type="button" class="button button--secondary" (click)="goBack()">
        {{ returnTo() === 'company' ? 'Back to Company' : 'Back to Contacts' }}
      </button>
      <button
        type="button"
        class="button button--ghost"
        (click)="viewServiceDetails()"
        [disabled]="serviceLoading()"
      >
        <ng-container *ngIf="!serviceLoading(); else detailServicePending">
          Service &amp; Product Details
        </ng-container>
      </button>
      <button
        type="button"
        class="button button--ghost"
        (click)="fetchLinkedInDetails()"
        [disabled]="linkedinLoading()"
      >
        <ng-container *ngIf="!linkedinLoading(); else linkedinFetchPending">
          {{ linkedinAvailable() ? 'View LinkedIn Details' : 'Fetch LinkedIn Details' }}
        </ng-container>
      </button>
    </div>
  </header>

  <nav class="breadcrumb">
    <a routerLink="/">Home</a>
    <span class="breadcrumb__separator">&gt;</span>
    <a [routerLink]="['/audiences', serviceId()]" *ngIf="serviceId() as sid; else cDetailAudienceFallback">
      Target Audience Profiles
    </a>
    <ng-template #cDetailAudienceFallback>
      <span>Target Audience Profiles</span>
    </ng-template>
    <span class="breadcrumb__separator">&gt;</span>
    <a [routerLink]="['/target-areas', serviceId()]" *ngIf="serviceId() as sid2; else cDetailTargetFallback">
      Target Areas
    </a>
    <ng-template #cDetailTargetFallback>
      <span>Target Areas</span>
    </ng-template>
    <span class="breadcrumb__separator">&gt;</span>
    <a
      *ngIf="serviceId() && locationId(); else cDetailCompanyFallback"
      [routerLink]="['/companies', serviceId(), locationId()]"
    >
      Target Company Intelligence
    </a>
    <ng-template #cDetailCompanyFallback>
      <span>Target Company Intelligence</span>
    </ng-template>
    <span class="breadcrumb__separator">&gt;</span>
    <a
      *ngIf="serviceId() && locationId(); else cDetailListFallback"
      [routerLink]="['/contacts', serviceId(), locationId()]"
    >
      Company Contact Details
    </a>
    <ng-template #cDetailListFallback>
      <span>Company Contact Details</span>
    </ng-template>
    <span class="breadcrumb__separator">&gt;</span>
    <span>Contact Detail</span>
  </nav>

  <section class="contact-detail-status">
    <span class="status status--error" *ngIf="errorMessage()">
      {{ errorMessage() }}
    </span>
    <span class="status status--success" *ngIf="statusMessage()">
      {{ statusMessage() }}
    </span>
  </section>

  <section class="contact-detail-card" *ngIf="record() as item">
    <dl class="contact-detail-grid">
      <div class="contact-detail-item">
        <dt>Company Name</dt>
        <dd>{{ item.company_name || 'N/A' }}</dd>
      </div>
      <div class="contact-detail-item">
        <dt>Website</dt>
        <dd>
          <a
            *ngIf="item.website; else noWebsite"
            [href]="item.website"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ item.website }}
          </a>
        </dd>
      </div>
      <div class="contact-detail-item">
        <dt>Company Size</dt>
        <dd>{{ item.company_size || 'N/A' }}</dd>
      </div>
      <div class="contact-detail-item">
        <dt>Contact Pages Found</dt>
        <dd>{{ item.contact_pages_found ?? 0 }}</dd>
      </div>
      <div class="contact-detail-item">
        <dt>Contact Pages Crawled</dt>
        <dd>
          <ng-container *ngIf="renderList(item.contact_pages_crawled).length; else noPages">
            <ul>
              <li *ngFor="let page of renderList(item.contact_pages_crawled)">
                {{ page }}
              </li>
            </ul>
          </ng-container>
        </dd>
      </div>
      <div class="contact-detail-item">
        <dt>Emails</dt>
        <dd>
          <ng-container *ngIf="renderList(item.emails).length; else noEmails">
            <ul>
              <li *ngFor="let email of renderList(item.emails)">
                {{ email }}
              </li>
            </ul>
          </ng-container>
        </dd>
      </div>
      <div class="contact-detail-item">
        <dt>Phone Numbers</dt>
        <dd>
          <ng-container *ngIf="renderList(item.phones).length; else noPhones">
            <ul>
              <li *ngFor="let phone of renderList(item.phones)">
                {{ phone }}
              </li>
            </ul>
          </ng-container>
        </dd>
      </div>
      <div class="contact-detail-item contact-detail-item--full">
        <dt>Social Media</dt>
        <dd>
          <ng-container *ngIf="renderSocialMedia(item.social_media).length; else noSocial">
            <ul>
              <li *ngFor="let pair of renderSocialMedia(item.social_media)">
                <strong>{{ pair.key }}:</strong> {{ pair.value }}
              </li>
            </ul>
          </ng-container>
        </dd>
      </div>
      <div class="contact-detail-item">
        <dt>Crawled Count</dt>
        <dd>{{ item.crawl_count ?? 0 }}</dd>
      </div>
      <div class="contact-detail-item">
        <dt>Pending Count</dt>
        <dd>{{ item.uncrawl_count ?? 0 }}</dd>
      </div>
    </dl>
  </section>

<section class="service-details" *ngIf="serviceLoading()">
    <span class="status status--info">
      <span class="spinner__dot"></span>
      <span>Fetching service and product information...</span>
    </span>
  </section>

  <section
    class="service-details"
    *ngIf="!serviceLoading() && (serviceDetails() || serviceError())"
  >
    <header class="service-details__header" *ngIf="serviceDetails() as details">
      <h2>Service &amp; Product Information</h2>
      <p>
        {{ details.company_name }}
        <ng-container *ngIf="details.website as site">
          &bull;
          <a
            [href]="site"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ site }}
          </a>
        </ng-container>
      </p>
    </header>

    <div *ngIf="serviceError()" class="status status--error">
      {{ serviceError() }}
    </div>

    <ng-container *ngIf="serviceDetails() as details">
      <div
        *ngIf="details.service_pages_crawled?.length"
        class="service-details__links"
      >
        <span>Pages scanned:</span>
        <ul>
          <li *ngFor="let link of details.service_pages_crawled">
            <a [href]="link" target="_blank" rel="noopener noreferrer">{{ link }}</a>
          </li>
        </ul>
      </div>

      <div class="service-details__content">
        <ng-container *ngIf="details.service_details.length; else noServiceDetails">
          <article
            class="service-details__section"
            *ngFor="let section of details.service_details"
          >
            <h3>{{ section.title }}</h3>
            <ul>
              <li *ngFor="let highlight of section.highlights">
                {{ highlight }}
              </li>
            </ul>
          </article>
        </ng-container>
      </div>
    </ng-container>
  </section>

  <ng-template #noWebsite>N/A</ng-template>
  <ng-template #noPages>N/A</ng-template>
  <ng-template #noEmails>N/A</ng-template>
  <ng-template #noPhones>N/A</ng-template>
  <ng-template #noSocial>N/A</ng-template>
  <ng-template #detailServicePending>
    <span class="spinner">
      <span class="spinner__dot"></span>
      <span>Fetching...</span>
    </span>
  </ng-template>
  <ng-template #linkedinFetchPending>
    <span class="spinner">
      <span class="spinner__dot"></span>
      <span>Crawling LinkedIn data, please wait...</span>
    </span>
  </ng-template>
  <ng-template #noServiceDetails>
    <p class="service-details__empty">
      No specific service or product descriptions were identified on the company website.
    </p>
  </ng-template>
</div>

<ng-template #loadingState>
  <div class="contact-detail-loading">
    <span class="spinner">
      <span class="spinner__dot"></span>
      <span>Loading contact details...</span>
    </span>
  </div>
</ng-template>
